import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { MdOutlineDescription, MdAdd } from 'react-icons/md';
import { useToast } from '@podus/ui';
import StatusBadge from '@/components/StatusBadge';
import CaseViewModal, { type UserCaseRow } from '@/components/CaseViewModal';
import { api } from '@/lib/api';
import { podusApi } from '@/lib/podusApi';
import { useMultidoorState } from '@/context/StateContext';
import type { Case } from '@/types';

/* ─── Types ─── */

type CaseStatus = 'Draft' | 'New' | 'Assigned' | 'In Progress' | 'Resolved' | 'Reopen';

interface UserCase {
    id: number;
    caseNumber: string;
    title: string;
    date: string;
    disputeCategory: string;
    evidence: number | string;
    daysInCourt: string;
    amount: string;
    status: CaseStatus;
}

/* ─── Component ─── */

interface InitiatePaymentResponse {
    txRef: string;
    checkoutUrl: string;
}

export default function UserDashboard() {
    const navigate = useNavigate();
    const toast = useToast();
    const { selectedState } = useMultidoorState();
    const [cases, setCases] = useState<UserCase[]>([]);
    const [selectedCase, setSelectedCase] = useState<UserCaseRow | null>(null);
    const [payingCaseId, setPayingCaseId] = useState<number | null>(null);

    const handlePayForDraft = async (caseId: number) => {
        if (payingCaseId) return;
        setPayingCaseId(caseId);
        try {
            const res = await api.get<Case>(`/api/cases/${caseId}`);
            const detail = res.response.data;
            if (!detail?.id) {
                toast.error('Could not load case');
                return;
            }
            const applicant = detail.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant');
            const redirectUrl = `${window.location.origin}/dashboard?payment=success&case=${detail.id}`;
            const payment = await podusApi.post<InitiatePaymentResponse>('/payments/initiate/flutterwave', {
                product: 'multidoor',
                productRef: String(detail.id),
                stateSlug: selectedState?.slug,
                amount: Number(detail.amountPaid) || 10000,
                currency: 'NGN',
                customer: {
                    email: applicant?.email || 'noreply@podus.org',
                    name: applicant?.name,
                    phone: applicant?.phone,
                },
                redirectUrl,
                metadata: {
                    caseNumber: detail.caseNumber,
                    disputeCategory: detail.disputeCategory,
                },
            });
            if (payment?.checkoutUrl) {
                window.location.href = payment.checkoutUrl;
            } else {
                toast.error('Could not start payment');
            }
        } catch (err) {
            console.error('Pay for draft failed:', err);
            toast.error('Could not start payment');
        } finally {
            setPayingCaseId(null);
        }
    };

    // When Flutterwave redirects back with ?tx_ref=... verify the payment so the
    // server updates the intent even if the webhook is delayed, then clean the URL.
    // useEffect(() => {
    //     const params = new URLSearchParams(window.location.search);
    //     const txRef = params.get('tx_ref');
    //     const status = params.get('status');
    //     if (!txRef) return;

    //     if (status === 'cancelled') {
    //         toast.info('Payment cancelled');
    //     } else {
    //         podusApi
    //             .get<{ txRef: string; status: string }>(`/payments/verify/flutterwave/${txRef}`)
    //             .then((data) => {
    //                 if (data.status === 'successful') toast.success('Payment confirmed');
    //                 else if (data.status === 'pending') toast.info('Payment is still processing');
    //                 else toast.error('Payment verification failed');
    //             })
    //             .catch(() => toast.error('Could not verify payment'));
    //     }

    //     // Strip the query params from the URL so refreshes don't re-verify.
    //     const url = new URL(window.location.href);
    //     ['tx_ref', 'transaction_id', 'status', 'payment', 'case'].forEach((k) => url.searchParams.delete(k));
    //     window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    // }, [toast]);

    useEffect(() => {
        api.get<{ cases: UserCase[]; totalExpenditure: number }>('/api/cases/user?limit=50')
            .then((res) => {
                const data = res.response.data;
                if (data && Array.isArray(data)) {
                    // API returns array of cases directly
                    setCases(
                        (
                            data as unknown as {
                                id: number;
                                caseNumber: string;
                                createdAt: string;
                                disputeCategory: string;
                                evidence: { length: number } | null;
                                amountPaid: number;
                                status: CaseStatus;
                                parties: { name: string; role: string }[];
                            }[]
                        ).map((c) => {
                            const applicant = c.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant');
                            const respondent = c.parties?.find((p) => p.role === 'Respondent');
                            return {
                                id: c.id,
                                caseNumber: c.caseNumber,
                                title:
                                    applicant && respondent ? `${applicant.name} vs ${respondent.name}` : c.caseNumber,
                                date: new Date(c.createdAt).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                }),
                                disputeCategory: c.disputeCategory,
                                evidence: c.evidence?.length ?? 'Nil',
                                daysInCourt: '—',
                                amount: `₦${Number(c.amountPaid).toLocaleString()}`,
                                status: c.status,
                            };
                        }),
                    );
                }
            })
            .catch(() => setCases([]));
    }, []);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
                        <h1 className="text-2xl font-bold text-secondary sm:text-3xl">MultiDoor User Dashboard</h1>
                        <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-primary px-3 py-0.5 text-[11px] font-medium text-primary">
                            Version 1.0
                        </span>
                    </div>
                    <p className="mt-1 text-[13px] text-[#888]">
                        Last updated on:{' '}
                        {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/dashboard/create-case')}
                    className="flex w-[200px] items-center justify-center gap-2 rounded-lg bg-secondary px-5 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-secondary-400"
                >
                    <MdOutlineDescription size={16} />
                    Create New Case
                </button>
            </div>

            {/* Case Cards */}

            <div className="space-y-4 max-w-2xl m-auto">
                {cases.length === 0 && (
                    <div className="flex flex-col items-center justify-center bg-white rounded-2xl border border-[#E0E0E0] py-16 text-center">
                        <MdOutlineDescription size={48} className="text-[#ddd]" />
                        <p className="mt-4 text-[15px] font-medium text-[#888]">No cases yet</p>
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard/create-case')}
                            className="mt-4 flex items-center gap-2 rounded-full bg-secondary px-6 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-secondary-400"
                        >
                            <MdAdd size={16} />
                            Create Your First Case
                        </button>
                    </div>
                )}

                {cases.map((c) => (
                    <div key={c.id} className="rounded-2xl border border-[#E0E0E0] bg-white p-5 sm:p-6">
                        {/* Title + Status */}
                        <div className="flex items-start justify-between gap-4">
                            <h3 className="text-lg font-bold text-secondary">{c.title}</h3>
                            <StatusBadge status={c.status} />
                        </div>

                        {/* Details grid */}
                        <div className="mt-4 grid grid-cols-2 gap-y-3 sm:grid-cols-3 bg-[#F5F5F5] p-4 rounded-lg">
                            <div>
                                <p className="text-[12px] text-[#888]">Date</p>
                                <p className="mt-0.5 text-[14px] font-bold text-secondary">{c.date}</p>
                            </div>
                            <div>
                                <p className="text-[12px] text-[#888]">Dispute Category</p>
                                <p className="mt-0.5 text-[14px] font-bold text-secondary">{c.disputeCategory}</p>
                            </div>
                            <div>
                                <p className="text-[12px] text-[#888]">Days in Court</p>
                                <p className="mt-0.5 text-[14px] font-bold text-secondary">{c.daysInCourt}</p>
                            </div>
                        </div>

                        {/* Amount + Actions */}
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                            <span className="rounded-lg border border-[#eee] px-4 py-1.5 text-[14px] font-bold text-secondary">
                                {c.amount}
                            </span>
                            <div className="flex items-center gap-2">
                                {c.status === 'Draft' && (
                                    <button
                                        type="button"
                                        onClick={() => handlePayForDraft(c.id)}
                                        disabled={payingCaseId === c.id}
                                        className="rounded-lg bg-primary px-5 py-2 text-[13px] font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {payingCaseId === c.id ? 'Starting…' : 'Pay Now'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setSelectedCase({
                                            id: c.id,
                                            caseNumber: c.caseNumber,
                                            title: c.title,
                                            date: c.date,
                                            disputeCategory: c.disputeCategory,
                                            amount: c.amount,
                                            status: c.status,
                                        })
                                    }
                                    className="rounded-lg bg-secondary px-5 py-2 text-[13px] font-medium text-white transition-colors hover:bg-secondary-400"
                                >
                                    View Details
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <CaseViewModal open={!!selectedCase} onClose={() => setSelectedCase(null)} caseData={selectedCase} />
        </div>
    );
}
